// ============================================================================
// REQUEST TYPE SERVICE - Digital Approval System
// ============================================================================
// Business logic and API calls for Request Type module

import { get, post, put, del } from '../client/axiosClient';
import { requestTypesEndpoints } from '../endpoints';
import { appendQueryParams } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ID,
  RequestTypeEntity,
  CreateRequestTypeRequest,
  UpdateRequestTypeRequest,
  RequestTypeQueryParameters,
  RequestTypeListResponse,
} from '../types';

/**
 * Request Type Service Class
 */
class RequestTypeService {
  /**
   * Get all request types with pagination and filtering
   */
  async getAll(params?: RequestTypeQueryParameters): Promise<RequestTypeListResponse> {
    const endpoint = params
      ? appendQueryParams(requestTypesEndpoints.list(), params)
      : requestTypesEndpoints.list();

    const response = await get<ApiResponse<RequestTypeListResponse>>(endpoint);
    return response.data.data;
  }

  /**
   * Get all active request types (most useful for forms)
   */
  async getActive(): Promise<RequestTypeEntity[]> {
    const response = await get<ApiResponse<RequestTypeEntity[]>>(requestTypesEndpoints.active());
    return response.data.data;
  }

  /**
   * Get request type by ID
   */
  async getById(id: ID): Promise<RequestTypeEntity> {
    const response = await get<ApiResponse<RequestTypeEntity>>(requestTypesEndpoints.getById(id));
    return response.data.data;
  }

  /**
   * Get request type by code
   */
  async getByCode(code: string): Promise<RequestTypeEntity> {
    const response = await get<ApiResponse<RequestTypeEntity>>(requestTypesEndpoints.getByCode(code));
    return response.data.data;
  }

  /**
   * Get request types by workflow template
   */
  async getByWorkflowTemplate(workflowTemplateId: ID): Promise<RequestTypeEntity[]> {
    const response = await get<ApiResponse<RequestTypeEntity[]>>(
      requestTypesEndpoints.getByWorkflowTemplate(workflowTemplateId)
    );
    return response.data.data;
  }

  /**
   * Create new request type
   */
  async create(data: CreateRequestTypeRequest): Promise<RequestTypeEntity> {
    const response = await post<ApiResponse<RequestTypeEntity>>(
      requestTypesEndpoints.create(),
      data
    );
    return response.data.data;
  }

  /**
   * Update request type
   */
  async update(id: ID, data: UpdateRequestTypeRequest): Promise<RequestTypeEntity> {
    const response = await put<ApiResponse<RequestTypeEntity>>(
      requestTypesEndpoints.update(id),
      data
    );
    return response.data.data;
  }

  /**
   * Delete request type
   */
  async delete(id: ID): Promise<{ success: boolean }> {
    const response = await del<ApiResponse<{ success: boolean }>>(
      requestTypesEndpoints.delete(id)
    );
    return response.data.data;
  }

  /**
   * Validate request type code uniqueness
   */
  async validateCode(code: string, excludeId?: ID): Promise<boolean> {
    const response = await get<ApiResponse<boolean>>(
      requestTypesEndpoints.validateCode(code, excludeId)
    );
    return response.data.data;
  }

  /**
   * Get active request types with custom filters (helper method)
   */
  async search(params: RequestTypeQueryParameters): Promise<RequestTypeListResponse> {
    return this.getAll(params);
  }
}

// Export singleton instance
export const requestTypeService = new RequestTypeService();
export default requestTypeService;
